import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { db, doc, getDoc } from '@/firebase';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface Banner {
  url: string;
  active: boolean;
}

export default function HomeSlider() {
  const [settings, setSettings] = React.useState<{ isActive: boolean; banners: Banner[] } | null>({
    isActive: true,
    banners: [
      { url: 'https://images.unsplash.com/photo-1541888086225-f673f41cde00?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80', active: true },
      { url: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80', active: true },
      { url: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80', active: true }
    ]
  });
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchSettings = async () => {
      try {
        const snap = await getDoc(doc(db, 'settings', 'home_slider'));
        if (snap.exists()) {
          setSettings(snap.data() as any);
        }
      } catch (error) {
        console.error('Error fetching home slider:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const activeBanners = settings?.banners?.filter(b => b.active) || [];

  React.useEffect(() => {
    if (activeBanners.length <= 1) return;
    
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % activeBanners.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [activeBanners.length]);

  if (loading || !settings?.isActive || activeBanners.length === 0) return null;

  const handleNext = () => setCurrentIndex((prev) => (prev + 1) % activeBanners.length);
  const handlePrev = () => setCurrentIndex((prev) => (prev - 1 + activeBanners.length) % activeBanners.length);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24 sm:mt-28 md:mt-32 mb-8">
      <div className="relative w-full aspect-[2.6/1] sm:aspect-[3/1] rounded-[1rem] sm:rounded-[2.5rem] overflow-hidden group shadow-2xl bg-card border border-border">
        <AnimatePresence mode="wait">
          <motion.img
            key={currentIndex}
            src={activeBanners[currentIndex].url}
            alt={`Banner ${currentIndex + 1}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full h-full object-contain bg-card"
            referrerPolicy="no-referrer"
          />
        </AnimatePresence>

        {activeBanners.length > 1 && (
          <>
            <button 
              onClick={handlePrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/20 backdrop-blur-md text-white opacity-0 group-hover:opacity-100 transition-all hover:bg-white/40"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button 
              onClick={handleNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/20 backdrop-blur-md text-white opacity-0 group-hover:opacity-100 transition-all hover:bg-white/40"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
            
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
              {activeBanners.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentIndex(i)}
                  className={`h-1.5 transition-all rounded-full ${
                    i === currentIndex ? 'w-8 bg-gold' : 'w-2 bg-white/50'
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
